import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Grid,
	GridProps,
	If,
	makeClassName,
	mappedArray,
	Row,
	StyleProps,
	useCount,
} from "@hex-insights/core";
import { Icon } from "../Icon";
import styles from "./styles.module.css";

export type PaginatedGridProps<T> = {
	items: T[] | null | undefined;
	columns: number;
	rows?: number;
	gap?: GridProps["gap"];
	children: (item: T) => React.ReactNode;
} & Partial<ClassNameProps & StyleProps>;

export function PaginatedGrid<T>({ items, columns, rows = 1, gap, className, style, children }: PaginatedGridProps<T>) {
	const pageSize = columns * rows;
	const numPages = Math.ceil((items?.length ?? pageSize) / pageSize);
	const {
		count: displayIndex,
		increment: incrementDisplayIndex,
		decrement: decrementDisplayIndex,
	} = useCount(0, { mod: numPages });

	const displayedItems = (items ?? []).slice(displayIndex * pageSize, (displayIndex + 1) * pageSize);

	return (
		<Column justify="spaced-start" verticalSpacing="0.5rem" className={className} style={style}>
			<Grid
				trackAxis="rows"
				tracks={rows}
				templateCrossTracks={`repeat(${columns}, 1fr)`}
				gap={gap}
				className={styles["paginated-grid__grid"]}
			>
				{displayedItems.map((e) => children(e))}
			</Grid>

			<If condition={numPages > 1}>
				<Row justify="center">
					<Row justify="spaced-start" align="center">
						<Button variant="link" size="small" onClick={decrementDisplayIndex}>
							<Icon.CaretLeft size={18} className="block" />
						</Button>
						<Row justify="spaced-center">
							{mappedArray(numPages, (i) => (
								<div
									key={i}
									className={makeClassName(
										styles["paginated-grid__page-dot"],
										i === displayIndex ? styles["paginated-grid__page-dot--active"] : "",
									)}
								/>
							))}
						</Row>
						<Button variant="link" size="small" onClick={incrementDisplayIndex}>
							<Icon.CaretRight size={18} className="block" />
						</Button>
					</Row>
				</Row>
			</If>
		</Column>
	);
}
